import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ImageUploadField from "../../Product/shared/ImageUploadField";
import { uploadCompanyLogo } from "../../../services";
import { CompanyProfile } from "../../../types/company-profile.type";

// The logo is cached on every phone and embedded in every document, so the API caps it small
// and accepts PNG / JPEG only; checking here saves a round trip for an obvious mistake.
const ACCEPTED_LOGO_TYPES = ["image/png", "image/jpeg"];
const MAX_LOGO_BYTES = 512 * 1024;
const LOGO_TYPE_ERROR = "รองรับเฉพาะไฟล์ PNG หรือ JPG";
const LOGO_SIZE_ERROR = "ไฟล์โลโก้ต้องมีขนาดไม่เกิน 512 KB";

interface CompanyLogoCardProps {
  /** The stored logo as a data URL; null while the documents print the bundled logo. */
  logoUrl: string | null;
  onUploaded: (profile: CompanyProfile) => void;
}

const checkLogoFile = (file: File): string | null => {
  if (!ACCEPTED_LOGO_TYPES.includes(file.type)) return LOGO_TYPE_ERROR;
  if (file.size > MAX_LOGO_BYTES) return LOGO_SIZE_ERROR;
  return null;
};

export default function CompanyLogoCard({ logoUrl, onUploaded }: Readonly<CompanyLogoCardProps>) {
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const handleSelectFile = (file: File) => {
    const error = checkLogoFile(file);
    setFileError(error);
    setPickedFile(error ? null : file);
  };

  const handleUpload = async () => {
    if (!pickedFile) return;
    setUploading(true);
    try {
      const { data: body } = await uploadCompanyLogo(pickedFile);
      if (body.data) onUploaded(body.data);
      setPickedFile(null);
    } catch {
      // Surfaced by the axios interceptor; keep the picked file so the upload can be retried.
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack gap={1.5}>
      <ImageUploadField
        label="โลโก้บริษัท (PNG หรือ JPG ไม่เกิน 512 KB)"
        imageUrl={logoUrl}
        file={pickedFile}
        accept={ACCEPTED_LOGO_TYPES.join(",")}
        onSelect={handleSelectFile}
      />
      {logoUrl ? null : (
        <Typography variant="caption" color="text.secondary">
          ยังไม่ได้อัปโหลดโลโก้ — เอกสารใช้โลโก้มาตรฐานของระบบ
        </Typography>
      )}
      {fileError ? <Alert severity="warning">{fileError}</Alert> : null}
      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={!pickedFile || uploading}
        sx={{ alignSelf: "flex-start" }}
      >
        {uploading ? "กำลังอัปโหลด..." : "อัปโหลดโลโก้"}
      </Button>
    </Stack>
  );
}
